/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import { MuiForm } from 'lib/Form';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { Row } from 'ui/Flex';
import { ILocationConfig } from 'components/location/models/LocationModel';

export interface LocationServicesFormData {
    book_room: boolean;
    book_table: boolean;
    checkins: boolean;
    offline_checkin: boolean;
    pre_order: boolean;
    dine_in: boolean;
    order_to_table: boolean;
    order_to_table_nfc: boolean;
    tab: boolean;
    pay_at_table: boolean;
    pay_at_table_nfc: boolean;
    delivery: boolean;
}

const OFFLINE_CHECKIN_DESCRIPTION =
    "This enables users to checkin and use rewards using a barcode. This is useful when the location doesn't have a good internet connection.";

const NFC_DESCRIPTION =
    'This enables users to use their (NFC enabled) phone to scan a tag, letting the app know what table they want to open an order at.';

export const servicesDefaults: LocationServicesFormData = {
    book_room: false,
    book_table: false,
    checkins: false,
    offline_checkin: false,
    pre_order: false,
    dine_in: false,
    order_to_table: false,
    order_to_table_nfc: false,
    tab: false,
    pay_at_table: false,
    pay_at_table_nfc: false,
    delivery: false
};

interface LocationServicesFormProps {
    open: boolean;
    onSubmit: (data: LocationServicesFormData) => void;
    onPrevious: () => void;
    initialValues: LocationServicesFormData;
    locationConfig: ILocationConfig;
}

export const LocationServicesForm: React.FC<LocationServicesFormProps> = ({
    open,
    onSubmit,
    onPrevious,
    initialValues,
    locationConfig
}) => {
    if (!open) {
        return null;
    }
    return (
        <MuiForm onSubmit={onSubmit} initialValues={initialValues}>
            {({ submitForm }) => (
                <Form>
                    <Box width="100%" sx={{ px: 3, pt: 1 }}>
                        <Typography variant="body2">
                            Configure what services you would like to be enabled at your location.
                        </Typography>
                    </Box>
                    <Box padding={3}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <SwitchFormField name="book_room" label="Room Bookings" />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField name="book_table" label="Table Bookings" />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField name="checkins" label="Checkins" />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField
                                    name="offline_checkin"
                                    label="Barcode Checkins & Rewards"
                                    description={OFFLINE_CHECKIN_DESCRIPTION}
                                />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField name="pre_order" label="Preorders" />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField name="dine_in" label="Dine in" />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField name="order_to_table" label="Order to Table" />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField
                                    name="order_to_table_nfc"
                                    label="Order to Table NFC Tags"
                                    description={NFC_DESCRIPTION}
                                />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField name="tab" label="Bar Tabs" />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField name="pay_at_table" label="Pay at Table" />
                            </Grid>
                            <Grid size={12}>
                                <SwitchFormField
                                    name="pay_at_table_nfc"
                                    label="Pay at Table NFC Tags"
                                    description={NFC_DESCRIPTION}
                                />
                            </Grid>
                            {locationConfig.deliveryEnabled && (
                                <Grid size={12}>
                                    <SwitchFormField name="delivery" label="Delivery" />
                                </Grid>
                            )}
                            <Grid size={12}>
                                <Row align="flex-end" gutter>
                                    <Button onClick={onPrevious} color="primary" variant="outlined">
                                        Back
                                    </Button>
                                    <Button onClick={submitForm} color="primary" variant="contained">
                                        Next
                                    </Button>
                                </Row>
                            </Grid>
                        </Grid>
                    </Box>
                </Form>
            )}
        </MuiForm>
    );
};
